import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import { Text } from '@atlaskit/primitives/text';

import { Show } from '@atlaskit/primitives/responsive/show';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--devai-acrabats/jira-rovo-dev-spotlight-walkthrough';

import { type ComponentType, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { messages } from '../../../components/messages';

import RovoDevIllustrationLight from '../../../components/assets/rovo-dev-illustration-light.svg';

import RovoDevIllustrationDark from '../../../components/assets/rovo-dev-illustration-dark.svg';

import Image from '@atlaskit/image';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightPrimaryLink } from '@atlaskit/spotlight/primary-link';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightMedia } from '@atlaskit/spotlight/media';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

interface FirstStepProps {
	dismiss: () => void;
	next: () => void;
}

interface LastStepProps {
	dismiss: () => void;
	done: () => void;
	back: () => void;
}

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<PlacementRenderProps>(),
		Component: ({ onDismiss }: PlacementRenderProps) => {
			const { onClosed, onStepChanged } = usePostOfficeUserInteractions();
			const { messageDismissed } = useMessageEvent();

			// Defer rendering until after first paint to avoid blocking critical path
			const [ready, setReady] = useState(false);

			useEffect(() => {
				const id = requestIdleCallback(() => setReady(true), { timeout: 100 });
				return () => cancelIdleCallback(id);
			}, []);

			/**
			 * This uses `0` to mean 'the tour is not in progress',
			 * which eliminates the need for some type-checking in the message,
			 * and keeps array indexes consistent with tour step numbers.
			 */
			const [step, setStep] = useState<number>(1);
			const renderInline = useMountToHtmlElement({
				selectors: `#jira-rovo-dev-spotlight-target`,
				as: 'inline',
			});

			// TODO(PO-9967): `dismiss` is shared by every mid-tour dismiss AND the final
			// step's `done` control (see `done={dismiss}` below), so it can't be split into
			// a terminal `onAcknowledged` call without a separate, larger change to give the
			// final control its own identifier/kind. Until then, both controls retain the
			// existing dismissed lifecycle action. Left out of batch E1-1 (PO-9967) as a
			// follow-up; see PR description.
			const dismiss = async () => {
				await messageDismissed();
				void onClosed({ interactionIdentifier: 'dismiss', additionalMetadata: {} });

				onDismiss();
			};

			const next = () => {
				void onStepChanged({
					interactionIdentifier: 'next',
					direction: 'next',
					fromStep: step,
					toStep: Math.min(tour.length - 1, step + 1),
				});
				setStep(Math.min(tour.length - 1, step + 1));
			};

			const back = async () => {
				void onStepChanged({
					interactionIdentifier: 'back',
					direction: 'previous',
					fromStep: step,
					toStep: Math.max(1, step - 1),
				});
				setStep(Math.max(1, step - 1));
			};

			const Step = tour[step] || (() => <></>);

			if (!ready) {
				return null;
			}

			return (
				<div>{renderInline(<Step dismiss={dismiss} next={next} done={dismiss} back={back} />)}</div>
			);
		},
	},
});

const SpotlightCardOne = ({ dismiss, next }: FirstStepProps) => {
	return (
		<SpotlightCard>
			<SpotlightHeader>
				<SpotlightHeadline>
					<FormattedMessage {...messages.exploreRovodevHeading} />
				</SpotlightHeadline>
				<SpotlightControls>
					<SpotlightDismissControl onClick={dismiss} />
				</SpotlightControls>
			</SpotlightHeader>
			<SpotlightMedia>
				<Image
					src={RovoDevIllustrationLight}
					srcDark={RovoDevIllustrationDark}
					alt="Rovo Dev Illustration"
				/>
			</SpotlightMedia>
			<SpotlightBody>
				<Text>
					<FormattedMessage {...messages.exploreRovodevDescription} />
				</Text>
			</SpotlightBody>
			<SpotlightFooter>
				<SpotlightStepCount>
					<FormattedMessage
						{...messages.progressIndicator}
						values={{ currentStep: 1, totalSteps: 2 }}
					/>
				</SpotlightStepCount>
				<SpotlightActions>
					<SpotlightPrimaryAction onClick={next}>
						<FormattedMessage {...messages.nextButton} />
					</SpotlightPrimaryAction>
				</SpotlightActions>
			</SpotlightFooter>
		</SpotlightCard>
	);
};

const SpotlightCardTwo = ({ dismiss, back }: LastStepProps) => {
	return (
		<SpotlightCard>
			<SpotlightHeader>
				<SpotlightHeadline>
					<FormattedMessage {...messages.understandRovodevHeading} />
				</SpotlightHeadline>
				<SpotlightControls>
					<SpotlightDismissControl onClick={dismiss} />
				</SpotlightControls>
			</SpotlightHeader>
			<SpotlightBody>
				<Text>
					<FormattedMessage {...messages.understandRovodevDescription} />
				</Text>
			</SpotlightBody>
			<SpotlightFooter>
				<SpotlightStepCount>
					<FormattedMessage
						{...messages.progressIndicator}
						values={{ currentStep: 2, totalSteps: 2 }}
					/>
				</SpotlightStepCount>
				<SpotlightActions>
					<SpotlightSecondaryAction onClick={back}>
						<FormattedMessage {...messages.backButton} />
					</SpotlightSecondaryAction>
					<SpotlightPrimaryLink
						href="https://support.atlassian.com/rovo/docs/work-with-rovo-dev-in-jira/"
						target="_blank"
						rel="noopener noreferrer"
					>
						<FormattedMessage {...messages.readMoreButton} />
					</SpotlightPrimaryLink>
				</SpotlightActions>
			</SpotlightFooter>
		</SpotlightCard>
	);
};

// no-op to prevent PopoverContent from auto-dismissing on outside click/escape.
// Only the SpotlightDismissControl (X button) should trigger dismissal.
const preventAutoDismiss = () => {};

const tour: (ComponentType<FirstStepProps> | ComponentType<LastStepProps>)[] = [
	// Tour not in progress
	() => <></>,

	// Step 1
	({ dismiss, next }: FirstStepProps) => {
		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* Dummy target */}
					<div />
				</PopoverTarget>
				<PopoverContent dismiss={preventAutoDismiss} placement="bottom-end" isVisible={true}>
					<SpotlightCardOne dismiss={dismiss} next={next} />
				</PopoverContent>
			</PopoverProvider>
		);
	},

	// Step 2
	({ dismiss, done, back }: LastStepProps) => {
		return (
			<>
				<Show above="lg">
					<PopoverProvider>
						<PopoverTarget>
							{/* Dummy target */}
							<div />
						</PopoverTarget>
						<PopoverContent dismiss={preventAutoDismiss} placement="bottom-end" isVisible={true}>
							<SpotlightCardTwo dismiss={dismiss} done={done} back={back} />
						</PopoverContent>
					</PopoverProvider>
				</Show>
				<Show below="lg">
					<PopoverProvider>
						<PopoverTarget>
							{/* Dummy target */}
							<div />
						</PopoverTarget>
						<PopoverContent dismiss={preventAutoDismiss} placement="bottom-end" isVisible={true}>
							<SpotlightCardTwo dismiss={dismiss} done={done} back={back} />
						</PopoverContent>
					</PopoverProvider>
				</Show>
			</>
		);
	},
];
